import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Command as CommandPrimitive } from 'cmdk';
import { Check } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePortalContainer } from '../../lib/portal-container';
import { cn } from '../../lib/utils';
import { CommandGroup, CommandInput, CommandItem, CommandList } from './command';
import { Skeleton } from './skeleton';
export function AutoComplete({ className, autoFocus, disabled, emptyMessage, icon, isLoading = false, onBlurCallBack, onInputChange, onOpenChange, onValueChange, options, placeholder, shouldFilter, value, }) {
    const inputRef = useRef(null);
    const portalContainer = usePortalContainer();
    const [isOpen, setIsOpen] = useState(false);
    const setOpen = useCallback((open) => {
        setIsOpen(open);
        onOpenChange?.(open);
    }, [onOpenChange]);
    const [selected, setSelected] = useState(value);
    const [inputValue, setInputValue] = useState(value?.label ?? '');
    useEffect(() => {
        setSelected(value);
        setInputValue(value?.label ?? '');
    }, [value]);
    // Notify parent of input changes
    const handleInputChange = useCallback((newValue) => {
        setInputValue(newValue);
        onInputChange?.(newValue);
    }, [onInputChange]);
    const handleKeyDown = useCallback((event) => {
        const input = inputRef.current;
        if (!input) {
            return;
        }
        // Keep the options displayed when the user is typing
        if (!isOpen) {
            setOpen(true);
        }
        // This is not a default behaviour of the <input /> field
        if (event.key === 'Enter' && input.value !== '') {
            const optionToSelect = options.find((option) => option.label === input.value);
            if (optionToSelect) {
                setSelected(optionToSelect);
                onValueChange?.(optionToSelect);
            }
        }
        if (event.key === 'Escape') {
            input.blur();
        }
        if (event.key === 'Backspace' && selected) {
            setSelected(undefined);
            setInputValue('');
            // @ts-expect-error clear selection
            onValueChange?.(undefined);
            return;
        }
    }, [isOpen, options, onValueChange, selected, setOpen]);
    const handleBlur = useCallback(() => {
        setOpen(false);
        setInputValue(selected?.label ?? '');
    }, [selected, setOpen]);
    const handleSelectOption = useCallback((selectedOption) => {
        setInputValue(selectedOption.label);
        setSelected(selectedOption);
        onValueChange?.(selectedOption);
        setOpen(false);
    }, [onValueChange, setOpen]);
    // compute rect for portal dropdown
    const [dropdownStyle, setDropdownStyle] = useState();
    useEffect(() => {
        if (!isOpen || !inputRef.current) {
            return;
        }
        const updateDropdownPosition = () => {
            if (!inputRef.current) {
                return;
            }
            const rect = inputRef.current.getBoundingClientRect();
            if (portalContainer !== null && portalContainer !== document.body) {
                const containerRect = portalContainer.getBoundingClientRect();
                setDropdownStyle({
                    left: rect.left - containerRect.left,
                    position: 'absolute',
                    top: rect.bottom - containerRect.top,
                    width: rect.width,
                    zIndex: 60,
                });
                return;
            }
            setDropdownStyle({
                left: rect.left + window.scrollX,
                position: 'absolute',
                top: rect.bottom + window.scrollY,
                width: rect.width,
                zIndex: 60,
            });
        };
        updateDropdownPosition();
        window.addEventListener('resize', updateDropdownPosition);
        window.addEventListener('scroll', updateDropdownPosition, true);
        return () => {
            window.removeEventListener('resize', updateDropdownPosition);
            window.removeEventListener('scroll', updateDropdownPosition, true);
        };
    }, [isOpen, portalContainer]);
    const dropdown = (_jsx("div", { className: cn('animate-in fade-in-0 zoom-in-95 border-input pointer-events-auto rounded-lg border bg-white shadow-md outline-none', isOpen && !disabled ? 'block' : 'hidden'), style: dropdownStyle, children: _jsxs(CommandList, { className: "rounded-lg", children: [isLoading ? (_jsx(CommandPrimitive.Loading, { children: _jsx("div", { className: "p-1", children: _jsx(Skeleton, { className: "h-8 w-full" }) }) })) : null, options.length > 0 && !isLoading ? (_jsx(CommandGroup, { children: options.map((option) => {
                        const isSelected = selected?.value === option.value;
                        const optionKey = option.key ?? option.value;
                        return (_jsxs(CommandItem, { className: cn('flex w-full cursor-pointer items-center gap-2 px-2 py-1.5 hover:bg-gray-100', option.isChild && 'airport-child', option.isChild && option.isLastChild && 'airport-child-last'), onMouseDown: (event) => {
                                event.preventDefault();
                                event.stopPropagation();
                            }, onSelect: () => {
                                handleSelectOption(option);
                            }, value: option.label, children: [_jsx("div", { className: "flex w-4 justify-center", children: option.icon ?? (isSelected ? _jsx(Check, { className: "h-4 w-4" }) : null) }), _jsx("span", { className: "flex-1 text-left", children: option.label })] }, optionKey));
                    }) })) : null, !isLoading ? (_jsx(CommandPrimitive.Empty, { className: "rounded-sm px-2 py-3 text-center text-sm text-gray-500 select-none", children: emptyMessage })) : null] }) }));
    return (_jsxs(CommandPrimitive, { className: "w-full", onKeyDown: handleKeyDown, shouldFilter: shouldFilter, children: [_jsx("div", { className: "w-full cursor-text", onClick: () => inputRef.current?.focus(), children: _jsx(CommandInput, { autoFocus: autoFocus, className: cn('border-input bg-background text-foreground focus-visible:border-ring focus-visible:ring-ring/20 flex h-9 w-full cursor-text rounded-lg border py-2 text-sm shadow-sm shadow-black/5 transition-shadow placeholder:text-gray-500 focus-visible:ring-[3px] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50', className), disabled: disabled, icon: icon, onBlur: () => {
                        handleBlur();
                        onBlurCallBack?.();
                    }, onFocus: (e) => {
                        setOpen(true);
                        e.target.select();
                    }, onValueChange: (s) => {
                        handleInputChange(s);
                    }, placeholder: placeholder, ref: inputRef, value: inputValue }) }), isOpen && createPortal(dropdown, portalContainer ?? document.body)] }));
}
